import { createFileRoute, getRouteApi } from "@tanstack/react-router";
import { supabase } from "@/integrations/supabase/client";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Wallet, ShieldCheck, User as UserIcon, Calendar, Search, Undo2 } from "lucide-react";

export const Route = createFileRoute("/admin/deposits")({
  head: () => ({ meta: [{ title: "Manual Deposits — Admin" }] }),
  component: ManualDepositsPage,
});

const adminRouteApi = getRouteApi("/admin");


type Preset = "today" | "yesterday" | "7d" | "30d" | "custom";
type RoleFilter = "all" | "admin" | "sub_admin";
type StatusFilter = "all" | "success" | "reversed";

// Asia/Kolkata ISO helpers (UTC+05:30)
const IST_OFFSET_MIN = 330;
function istTodayYmd(): string {
  const d = new Date(Date.now() + IST_OFFSET_MIN * 60_000);
  return d.toISOString().slice(0, 10);
}
function ymdIstToUtcIso(ymd: string, endOfDay = false): string {
  const [y, m, d] = ymd.split("-").map(Number);
  const utcMs = Date.UTC(y, m - 1, d, endOfDay ? 23 : 0, endOfDay ? 59 : 0, endOfDay ? 59 : 0, endOfDay ? 999 : 0)
    - IST_OFFSET_MIN * 60_000;
  return new Date(utcMs).toISOString();
}
function formatIst(iso: string): string {
  return new Date(iso).toLocaleString("en-IN", { timeZone: "Asia/Kolkata" });
}

function ManualDepositsPage() {
  const qc = useQueryClient();
  const { role, user } = adminRouteApi.useRouteContext();
  const isSubAdmin = role === "sub_admin";
  const currentUserId = user?.id ?? null;
  const [preset, setPreset] = useState<Preset>("7d");

  const today = istTodayYmd();
  const [from, setFrom] = useState<string>(() => {
    const d = new Date(Date.now() - 6 * 864e5 + IST_OFFSET_MIN * 60_000);
    return d.toISOString().slice(0, 10);
  });
  const [to, setTo] = useState<string>(today);
  const [roleFilter, setRoleFilter] = useState<RoleFilter>("all");
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("all");
  const [adminId, setAdminId] = useState<string>("all");
  const [userQuery, setUserQuery] = useState("");
  const [minAmt, setMinAmt] = useState<string>("");
  const [maxAmt, setMaxAmt] = useState<string>("");
  const [reverseFor, setReverseFor] = useState<any>(null);
  const [reverseReason, setReverseReason] = useState("");
  const [reverseBusy, setReverseBusy] = useState(false);

  function applyPreset(p: Preset) {
    setPreset(p);
    const t = istTodayYmd();
    if (p === "today") { setFrom(t); setTo(t); }
    else if (p === "yesterday") {
      const y = new Date(Date.now() - 864e5 + IST_OFFSET_MIN * 60_000).toISOString().slice(0, 10);
      setFrom(y); setTo(y);
    } else if (p === "7d") {
      const d = new Date(Date.now() - 6 * 864e5 + IST_OFFSET_MIN * 60_000).toISOString().slice(0, 10);
      setFrom(d); setTo(t);
    } else if (p === "30d") {
      const d = new Date(Date.now() - 29 * 864e5 + IST_OFFSET_MIN * 60_000).toISOString().slice(0, 10);
      setFrom(d); setTo(t);
    }
  }

  const rangeStart = useMemo(() => ymdIstToUtcIso(from, false), [from]);
  const rangeEnd = useMemo(() => ymdIstToUtcIso(to, true), [to]);

  // Pull admin_credit ledger rows in range + join profiles.
  const { data: rows, isFetching } = useQuery({
    queryKey: ["manual_deposits", rangeStart, rangeEnd],
    refetchInterval: 30_000,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("wallet_transactions")
        .select("id, user_id, amount, status, note, meta, created_at, profiles:user_id(name, phone, wallet_balance)")
        .eq("type", "admin_credit")
        .eq("status", "success")
        .gte("created_at", rangeStart)
        .lte("created_at", rangeEnd)
        .order("created_at", { ascending: false })
        .limit(1000);
      if (error) throw error;
      const list = (data ?? []) as any[];
      // Enrich with admin profile + audit row.
      const adminIds = Array.from(new Set(list.map((r) => r?.meta?.admin_id).filter(Boolean)));
      const txIds = list.map((r) => r.id);
      const [adminProfs, roleRows, audits] = await Promise.all([
        adminIds.length
          ? supabase.from("profiles").select("id, name, phone, email").in("id", adminIds as string[])
          : Promise.resolve({ data: [] as any[] }),
        adminIds.length
          ? supabase.from("user_roles").select("user_id, role").in("user_id", adminIds as string[])
          : Promise.resolve({ data: [] as any[] }),
        txIds.length
          ? supabase.from("admin_wallet_adjustments").select("id, wallet_tx_id").in("wallet_tx_id", txIds)
          : Promise.resolve({ data: [] as any[] }),
      ]);
      const adminMap = new Map<string, any>((adminProfs.data ?? []).map((p: any) => [p.id, p]));
      const roleMap = new Map<string, string[]>();
      for (const rr of (roleRows.data ?? []) as any[]) {
        const arr = roleMap.get(rr.user_id) ?? [];
        arr.push(rr.role);
        roleMap.set(rr.user_id, arr);
      }
      const auditMap = new Map<string, string>((audits.data ?? []).map((a: any) => [a.wallet_tx_id, a.id]));
      return list.map((r) => {
        const admId = r?.meta?.admin_id;
        const roles = admId ? roleMap.get(admId) ?? [] : [];
        const adminRole: "admin" | "sub_admin" | "unknown" =
          r?.meta?.admin_role === "admin" || roles.includes("admin")
            ? "admin"
            : r?.meta?.admin_role === "sub_admin" || roles.includes("sub_admin")
              ? "sub_admin"
              : "unknown";
        return {
          ...r,
          admin_profile: admId ? adminMap.get(admId) : null,
          admin_role: adminRole,
          audit_id: auditMap.get(r.id) ?? null,
        };
      });
    },
  });

  // Reversal linkage: admin_debit rows referencing these credits.
  const { data: reversalMap } = useQuery({
    queryKey: ["manual_deposit_reversals", rangeStart, rangeEnd],
    refetchInterval: 30_000,
    queryFn: async () => {
      const { data } = await supabase
        .from("wallet_transactions")
        .select("id, created_at, note, meta")
        .eq("type", "admin_debit")
        .eq("status", "success")
        .gte("created_at", rangeStart)
        .limit(1000);
      const map = new Map<string, any>();
      for (const r of (data ?? []) as any[]) {
        const orig = r?.meta?.reverses_tx_id;
        if (orig) map.set(orig, r);
      }
      return map;
    },
  });

  // Filtered rows for table + summary.
  const filtered = useMemo(() => {
    if (!rows) return [];
    const q = userQuery.trim().toLowerCase();
    const min = minAmt ? Number(minAmt) : null;
    const max = maxAmt ? Number(maxAmt) : null;
    return rows.filter((r: any) => {
      if (isSubAdmin && r?.meta?.admin_id !== currentUserId) return false;

      if (roleFilter !== "all" && r.admin_role !== roleFilter) return false;
      if (adminId !== "all" && r?.meta?.admin_id !== adminId) return false;
      const reversed = !!reversalMap?.get(r.id);
      if (statusFilter === "success" && reversed) return false;
      if (statusFilter === "reversed" && !reversed) return false;
      const amt = Number(r.amount);
      if (min !== null && amt < min) return false;
      if (max !== null && amt > max) return false;
      if (q) {
        const hay = [r.profiles?.name, r.profiles?.phone, r.user_id, r.id, r?.meta?.reference]
          .map((s) => String(s ?? "").toLowerCase()).join(" ");
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [rows, reversalMap, userQuery, minAmt, maxAmt, roleFilter, adminId, statusFilter, isSubAdmin, currentUserId]);

  const adminOptions = useMemo(() => {
    const map = new Map<string, { id: string; name: string; phone?: string }>();
    for (const r of rows ?? []) {
      const id = r?.meta?.admin_id;
      if (!id) continue;
      if (!map.has(id)) {
        map.set(id, { id, name: r.admin_profile?.name ?? "Admin", phone: r.admin_profile?.phone });
      }
    }
    return Array.from(map.values());
  }, [rows]);

  const summary = useMemo(() => {
    const active = filtered.filter((r: any) => !reversalMap?.get(r.id));
    const totalCount = active.length;
    const totalAmount = active.reduce((s: number, r: any) => s + Number(r.amount), 0);
    const mainCount = active.filter((r: any) => r.admin_role === "admin").length;
    const mainAmount = active.filter((r: any) => r.admin_role === "admin")
      .reduce((s: number, r: any) => s + Number(r.amount), 0);
    const subCount = active.filter((r: any) => r.admin_role === "sub_admin").length;
    const subAmount = active.filter((r: any) => r.admin_role === "sub_admin")
      .reduce((s: number, r: any) => s + Number(r.amount), 0);
    const t = istTodayYmd();
    const todayActive = active.filter((r: any) =>
      new Date(r.created_at).toLocaleDateString("en-CA", { timeZone: "Asia/Kolkata" }) === t
    );
    return {
      totalCount, totalAmount,
      mainCount, mainAmount,
      subCount, subAmount,
      todayCount: todayActive.length,
      todayAmount: todayActive.reduce((s: number, r: any) => s + Number(r.amount), 0),
    };
  }, [filtered, reversalMap]);

  async function submitReversal() {
    if (!reverseFor) return;
    if (reverseReason.trim().length < 3) return toast.error("Reason must be at least 3 characters.");
    setReverseBusy(true);
    const { error } = await supabase.rpc("admin_reverse_wallet_credit" as any, {
      _wallet_tx_id: reverseFor.id,
      _reason: reverseReason.trim(),
    } as any);
    setReverseBusy(false);
    if (error) return toast.error(error.message);
    toast.success("Credit reversed.");
    setReverseFor(null);
    setReverseReason("");
    qc.invalidateQueries();
  }

  const cards = isSubAdmin
    ? [
        { label: "My Credits (Range)", value: summary.totalCount, sub: `₹${summary.totalAmount.toLocaleString("en-IN")}`, Icon: Wallet, tone: "text-primary" },
        { label: "Amount Credited", value: `₹${summary.totalAmount.toLocaleString("en-IN")}`, sub: `${summary.totalCount} tx`, Icon: Wallet, tone: "text-success" },
        { label: "Today's Credits (IST)", value: summary.todayCount, sub: `₹${summary.todayAmount.toLocaleString("en-IN")}`, Icon: Calendar, tone: "text-accent-foreground" },
      ]
    : [
        { label: "Total Manual Deposits", value: summary.totalCount, sub: `₹${summary.totalAmount.toLocaleString("en-IN")}`, Icon: Wallet, tone: "text-primary" },
        { label: "Total Amount Credited", value: `₹${summary.totalAmount.toLocaleString("en-IN")}`, sub: `${summary.totalCount} tx`, Icon: Wallet, tone: "text-success" },
        { label: "Main Admin Credits", value: summary.mainCount, sub: `₹${summary.mainAmount.toLocaleString("en-IN")}`, Icon: ShieldCheck, tone: "text-primary" },
        { label: "Sub-admin Credits", value: summary.subCount, sub: `₹${summary.subAmount.toLocaleString("en-IN")}`, Icon: UserIcon, tone: "text-warning" },
        { label: "Today's Credits (IST)", value: summary.todayCount, sub: `₹${summary.todayAmount.toLocaleString("en-IN")}`, Icon: Calendar, tone: "text-accent-foreground" },
      ];


  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-2xl font-black">Manual Deposits</h1>
        <p className="text-xs text-muted-foreground">
          Wallet credits added manually by admins after Telegram/WhatsApp verification.
        </p>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-2 gap-2 md:grid-cols-5">
        {cards.map((c) => (
          <div key={c.label} className="rounded-xl bg-card p-3 shadow-card">
            <div className={`flex items-center gap-1 text-[10px] font-bold uppercase ${c.tone}`}>
              <c.Icon className="h-3 w-3" /> {c.label}
            </div>
            <p className="mt-1 text-lg font-black">{c.value}</p>
            <p className="text-[10px] text-muted-foreground">{c.sub}</p>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div className="rounded-2xl bg-card p-4 shadow-card space-y-3">
        <div className="flex flex-wrap items-center gap-2">
          {(["today", "yesterday", "7d", "30d", "custom"] as Preset[]).map((p) => (
            <button
              key={p}
              onClick={() => applyPreset(p)}
              className={`rounded-lg px-2.5 py-1 text-xs font-bold ${preset === p ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground"}`}
            >
              {p === "7d" ? "Last 7d" : p === "30d" ? "Last 30d" : p[0].toUpperCase() + p.slice(1)}
            </button>
          ))}
          <div className="ml-auto flex items-center gap-1">
            <Input type="date" value={from} onChange={(e) => { setFrom(e.target.value); setPreset("custom"); }} className="h-8 w-auto text-xs" />
            <span className="text-[10px] text-muted-foreground">to</span>
            <Input type="date" value={to} onChange={(e) => { setTo(e.target.value); setPreset("custom"); }} className="h-8 w-auto text-xs" />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-2 md:grid-cols-5">
          {!isSubAdmin && (
            <>
              <div>
                <Label className="text-[10px]">Admin Role</Label>
                <select value={roleFilter} onChange={(e) => setRoleFilter(e.target.value as RoleFilter)}
                  className="h-8 w-full rounded-md border bg-background px-2 text-xs">
                  <option value="all">All</option>
                  <option value="admin">Main Admin</option>
                  <option value="sub_admin">Sub-admin</option>
                </select>
              </div>
              <div>
                <Label className="text-[10px]">Specific Admin</Label>
                <select value={adminId} onChange={(e) => setAdminId(e.target.value)}
                  className="h-8 w-full rounded-md border bg-background px-2 text-xs">
                  <option value="all">All admins</option>
                  {adminOptions.map((a) => (
                    <option key={a.id} value={a.id}>{a.name}{a.phone ? ` (${a.phone})` : ""}</option>
                  ))}
                </select>
              </div>
            </>
          )}

          <div>
            <Label className="text-[10px]">Status</Label>
            <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value as StatusFilter)}
              className="h-8 w-full rounded-md border bg-background px-2 text-xs">
              <option value="all">All</option>
              <option value="success">Success</option>
              <option value="reversed">Reversed</option>
            </select>
          </div>
          <div>
            <Label className="text-[10px]">Min ₹</Label>
            <Input type="number" value={minAmt} onChange={(e) => setMinAmt(e.target.value)} className="h-8 text-xs" placeholder="0" />
          </div>
          <div>
            <Label className="text-[10px]">Max ₹</Label>
            <Input type="number" value={maxAmt} onChange={(e) => setMaxAmt(e.target.value)} className="h-8 text-xs" placeholder="∞" />
          </div>
        </div>

        <div className="relative">
          <Search className="pointer-events-none absolute left-2 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            className="pl-8"
            placeholder="Search by user name, mobile, user ID, tx ID, or reference…"
            value={userQuery}
            onChange={(e) => setUserQuery(e.target.value)}
          />
        </div>
      </div>

      {/* Table */}
      <div className="rounded-2xl bg-card p-4 shadow-card">
        <div className="mb-3 flex items-center justify-between">
          <h3 className="text-sm font-bold">Transactions ({filtered.length})</h3>
          {isFetching && <span className="text-[10px] text-muted-foreground">Refreshing…</span>}
        </div>
        {filtered.length === 0 ? (
          <p className="py-8 text-center text-sm text-muted-foreground">
            No manual credits match these filters.
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[900px] text-xs">
              <thead className="bg-muted/50 text-left uppercase text-[10px] text-muted-foreground">
                <tr>
                  <th className="px-2 py-2">Date (IST)</th>
                  <th className="px-2 py-2">User</th>
                  <th className="px-2 py-2 text-right">Amount</th>
                  <th className="px-2 py-2 text-right">Old → New</th>
                  <th className="px-2 py-2">Credited By</th>
                  <th className="px-2 py-2">Source</th>
                  <th className="px-2 py-2">Reason</th>
                  <th className="px-2 py-2">IDs</th>
                  <th className="px-2 py-2">Status</th>
                  <th className="px-2 py-2"></th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((r: any) => {
                  const rev = reversalMap?.get(r.id);
                  const src = r?.meta?.contact_source as string | null;
                  const ref = r?.meta?.reference as string | null;
                  return (
                    <tr key={r.id} className={`border-t ${rev ? "opacity-70" : ""}`}>
                      <td className="px-2 py-2 whitespace-nowrap">{formatIst(r.created_at)}</td>
                      <td className="px-2 py-2">
                        <p className="font-semibold">{r.profiles?.name ?? "—"}</p>
                        <p className="text-[10px] text-muted-foreground">{r.profiles?.phone ?? "—"}</p>
                        <p className="font-mono text-[9px] text-muted-foreground">{r.user_id.slice(0, 8)}…</p>
                      </td>
                      <td className="px-2 py-2 text-right font-black">₹{Number(r.amount).toLocaleString("en-IN")}</td>
                      <td className="px-2 py-2 text-right text-[11px] text-muted-foreground">
                        ₹{Number(r?.meta?.old_balance ?? 0).toLocaleString("en-IN")} →{" "}
                        <span className="font-semibold text-foreground">₹{Number(r?.meta?.new_balance ?? 0).toLocaleString("en-IN")}</span>
                      </td>
                      <td className="px-2 py-2">
                        <p className="font-semibold">{r.admin_profile?.name ?? "—"}</p>
                        <p className="text-[10px] text-muted-foreground">
                          {r.admin_profile?.phone ?? r.admin_profile?.email ?? "—"}
                        </p>
                        <span className={`inline-block rounded px-1.5 py-0.5 text-[9px] font-bold uppercase ${
                          r.admin_role === "admin"
                            ? "bg-primary/15 text-primary"
                            : r.admin_role === "sub_admin"
                              ? "bg-warning/15 text-warning"
                              : "bg-muted text-muted-foreground"
                        }`}>
                          {r.admin_role === "admin" ? "Main Admin" : r.admin_role === "sub_admin" ? "Sub-admin" : "Unknown"}
                        </span>
                      </td>
                      <td className="px-2 py-2 whitespace-nowrap">
                        <span className="rounded bg-muted px-1.5 py-0.5 text-[10px] font-semibold uppercase">
                          {src === "other_manual" ? "Other" : src ?? "—"}
                        </span>
                        {ref && <p className="mt-1 font-mono text-[10px] text-muted-foreground">{ref}</p>}
                      </td>
                      <td className="px-2 py-2 max-w-[220px]">
                        <p className="line-clamp-3 text-[11px]">{r.note ?? "—"}</p>
                      </td>
                      <td className="px-2 py-2 font-mono text-[10px] text-muted-foreground">
                        <p>tx: {String(r.id).slice(0, 8)}…</p>
                        {r.audit_id && <p>audit: {String(r.audit_id).slice(0, 8)}…</p>}
                      </td>
                      <td className="px-2 py-2">
                        {rev ? (
                          <div>
                            <span className="rounded-full bg-destructive/15 px-2 py-0.5 text-[9px] font-bold uppercase text-destructive">
                              Reversed
                            </span>
                            <p className="mt-1 text-[10px] text-muted-foreground">
                              {formatIst(rev.created_at)}
                            </p>
                            {rev?.meta?.reason && (
                              <p className="text-[10px] text-destructive/80">{rev.meta.reason}</p>
                            )}
                          </div>
                        ) : (
                          <span className="rounded-full bg-success/15 px-2 py-0.5 text-[9px] font-bold uppercase text-success">
                            Success
                          </span>
                        )}
                      </td>
                      <td className="px-2 py-2">
                        {!rev && !isSubAdmin && (
                          <button
                            onClick={() => { setReverseFor(r); setReverseReason(""); }}
                            className="rounded border border-destructive/40 px-2 py-1 text-[10px] font-bold text-destructive hover:bg-destructive/10"
                          >
                            <Undo2 className="mr-0.5 inline h-3 w-3" /> Reverse
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Reverse dialog */}
      <Dialog open={!!reverseFor} onOpenChange={(v) => { if (!v) { setReverseFor(null); setReverseReason(""); } }}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Reverse Manual Credit</DialogTitle>
          </DialogHeader>
          {reverseFor && (
            <div className="space-y-3">
              <div className="rounded-lg bg-muted/40 p-3 text-xs space-y-1">
                <p><span className="font-semibold">User:</span> {reverseFor.profiles?.name} ({reverseFor.profiles?.phone})</p>
                <p><span className="font-semibold">Amount:</span> ₹{Number(reverseFor.amount).toLocaleString("en-IN")}</p>
                <p><span className="font-semibold">Credited on:</span> {formatIst(reverseFor.created_at)}</p>
                <p className="text-[10px] text-muted-foreground">
                  A matching debit ledger entry will be created. The original credit is preserved.
                </p>
              </div>
              <div>
                <Label className="text-xs">Reversal reason (visible in audit)</Label>
                <Input value={reverseReason} onChange={(e) => setReverseReason(e.target.value)}
                  placeholder="e.g. Duplicate credit, wrong user" />
              </div>
              <Button variant="destructive" className="w-full" onClick={submitReversal} disabled={reverseBusy}>
                {reverseBusy ? "Processing…" : "Confirm Reversal"}
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
